import React, { useState, useEffect } from 'react';
import { View, Text, Image, StyleSheet, StyleProp, ViewStyle, ImageStyle, ImageSourcePropType, Platform } from 'react-native';
import { getInitials, avatarColor } from '../utils/avatarColor';
import { fontWeights } from '../styles/typographyTokens';
import { contrastRatio } from '../utils/colorShade';

/** Белые инициалы, если держат 4.5:1 на кружке, иначе тёмные #1F1A17. Натив — прежний белый. */
const initialsOn = (bg: string): string => (
  Platform.OS === 'web' && /^#[0-9a-f]{6}$/i.test(bg) && contrastRatio('#FFFFFF', bg) < 4.5 ? '#1F1A17' : '#FFFFFF'
);

interface Props {
  /** Final image URL (already resolved via getFileUrl). Empty/null/failed → initials. */
  uri?: string | null;
  /**
   * Локальный ассет вместо URL — побеждает `uri`. Нужен там, где лицо задаёт
   * бренд, а не запись на бэкенде: у Agapella в списке, шапке и баблах должна
   * стоять утверждённая иконка (`AGAPELLA_AI_ICON`), какой бы `avatarUrl` ни
   * прислал сервер — на вебе это правило уже действует (ChatApiCompanionPanel).
   */
  source?: ImageSourcePropType;
  /** Display name — used for the initials and (by default) the colour seed. */
  name?: string | null;
  size?: number;
  /** Stable colour seed; defaults to `name`. Pass the user id for best stability. */
  seed?: string | null;
  style?: StyleProp<ViewStyle & ImageStyle>;
}

/**
 * User avatar with an automatic fallback: when there's no usable photo (no URL,
 * or the image fails to load) it renders a circle in a deterministic colour
 * (from the seed) with the user's initials.
 */
export const Avatar: React.FC<Props> = ({ uri, source, name, size = 40, seed, style }) => {
  const [failed, setFailed] = useState(false);
  // Reset the failed flag if the URL changes (e.g. list recycling).
  useEffect(() => setFailed(false), [uri]);

  const base = { width: size, height: size, borderRadius: size / 2 };
  const hasPhoto = !!uri && uri.trim().length > 0 && !failed;

  if (source) {
    return <Image source={source} style={[base, style]} />;
  }

  if (hasPhoto) {
    return <Image source={{ uri: uri as string }} style={[base, style]} onError={() => setFailed(true)} />;
  }

  return (
    <View style={[base, styles.fallback, { backgroundColor: avatarColor(seed ?? name) }, style]}>
      {/* CORE-1733 (возврат QA) — белые инициалы на светлых тонах палитры (золотой
          #C7902A — 2.8:1) не читались: цвет по контрасту с самим кружком. */}
      <Text style={[styles.initials, { fontSize: Math.round(size * 0.4), color: initialsOn(avatarColor(seed ?? name)) }]} numberOfLines={1}>
        {getInitials(name)}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  fallback: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  initials: {
    color: '#FFFFFF',
    fontWeight: fontWeights.bold,
  },
});
